import React, { useState, useContext, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { App, Alert, Button, Col, DatePicker, Divider, Form, Input, Modal, Row, Select, Tooltip, Typography } from 'antd'
import { WarningOutlined, InfoCircleOutlined, LoadingOutlined, SlackOutlined, WindowsFilled, GoogleOutlined, MailOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'
import dayjs, { Dayjs } from 'dayjs'
import { useSelector } from 'react-redux'
import isBoolean from 'lodash/isBoolean'
import isEqual from 'lodash/isEqual'
import isNull from 'lodash/isNull'
import omit from 'lodash/omit'
import sortBy from 'lodash/sortBy'
import capitalize from 'lodash/capitalize'
import { captureException } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { track } from '../../../services/analytics/analytics'
import { addMinutes, isAfter } from 'date-fns'

import * as logger from '../../../services/logger'
import Api from '@vacationtracker/shared/services/api'
import { getCompanyGeneral, getGoogleScopes } from '../../../graphql/custom-queries'
import { MicrosoftAuth } from '../../../services/auth/microsoft/microsoft'
import { SlackAuth } from '../../../services/auth/slack/slack'
import { GoogleAuth } from '../../../services/auth/google/google'
import { AUTOMATIC_USERS_IMPORT_LIMIT, GOOGLE_READ_USER_PERMISSION } from '../../../constants'
import { openSupportChat } from '../../../util/open-support-chat'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { selectFeatureFlagsSlice } from '../../../store/feature-flags-slice'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { selectAuthCompanySlice, setAuthCompany, isMicrosoftPayment } from '../../../store/auth-company-slice'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { notificationStore } from '../../../context/notificationsContext/store'
import { getIntegrationPlatformName } from '@vacationtracker/shared/functions/get-integration-platform-name'
import { validateCalculationStartYear } from '@vacationtracker/shared/functions/validate-calculation-start-year'
import { useGoogleAutoImportValidation } from '../../../hooks/use-google-auto-import-validation'
import { useGoogleDirectoryConnection } from '../../../hooks/use-google-directory-connection'

import IntlMessages from '../../../util/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import CircularProgress from '../../../components/circular-progress'
import ConfigureImportUsersOptionsForm, { getWelcomeMessageOptionsDefault, getDontSendWelcomeOption } from '../../../components/configure-import-users-options-form'
import showConfirmCompanySettings from '../../../components/confirm-company-settings-and-import-users-update-modal'
import { GoogleDirectoryConnection } from '../../../components/google-directory-connection'
import { MigrationFeatureModal } from '../../../components/migration-modal'
import PlatformSwitchModal from '../../../components/platform-switch-modal'
import YesNoSwitch from '../../../components/yes-no-switch'
import { WelcomeMessageOptions } from '../../Users/ImportUsersPage/types'

import { ICompanySettingsBody, ICompanySettingsFormValues } from './types'
import { IGetCompanyGeneral, SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { allPlatforms, MIGRATION_AVAILABLE_PLATFORMS } from '../../../constants'
import { IGetLocationsShort } from '../../../types/locations'
import { IGetTeamsShort } from '../../../types/teams'
import { ISlackUser } from '@vacationtracker/shared/types/repository/slack-api-repository'
import { SLACK_REQUIRED_BOT_SCOPES, SLACK_REQUIRED_USER_SCOPES } from '@vacationtracker/shared/data/slack'
import { IGetGoogleScopes } from '../../../types/custom-queries'
import { ConfigureImportSourceEnum, ISelectedLeaveType } from '../../../components/configure-import-users-options-form/types'
import { Platform } from '@vacationtracker/shared/types/core-event'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { FrontendUrls } from '../../../types/urls'
import { userHasWorkingHoursSchedule } from '@vacationtracker/shared/functions/work-week'
import { shouldShowMigrationSection } from '../../../routes/MigrationPage/util'

const { Text, Title, Paragraph } = Typography

if (!process.env.REACT_APP_MICROSOFT_CLIENT_ID) {
  throw new Error('Client IDs are required')
}

const msAuth = new MicrosoftAuth(process.env.REACT_APP_MICROSOFT_CLIENT_ID as string)
const slackAuth = new SlackAuth(process.env.REACT_APP_SLACK_CLIENT_ID as string)
const googleAuth = new GoogleAuth(process.env.REACT_APP_GOOGLE_CLIENT_ID as string)

const iconPadding = {
  paddingRight: '5px',
}

const platformIconMap = {
  slack: <SlackOutlined style={iconPadding} />,
  microsoft: <WindowsFilled style={iconPadding} />,
  google: <GoogleOutlined style={iconPadding} />,
  email: <MailOutlined style={iconPadding} />,
}

const GeneralPage: React.FC = () => {
  const { modal, notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const history = useHistory()

  const supportLink = 'https://vacationtracker.io/knowledge-base/google-workspace-editing-permissions/'
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const isMicrosoftBillingPayment = useSelector(isMicrosoftPayment)
  const shouldEnableHours = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.hours)
  const [shouldDisableDailyTracking, setShouldDisableDailyTracking] = useState(true)
  const [totalNumberOfUsers, setTotalNumberOfUsers] = useState(0)
  const shouldEnableCalculationStartYear = useShouldEnableFeatures('Core', FeatureFlagEnum.calculationStartYear, true)

  const [submitLoader, setSubmitLoader] = useState(false)
  const [msTenantId, setMsTenantId] = useState('')
  const [showEnableGoogleApiModal, setShowEnableGoogleApiModal] = useState(false)
  const [companyValue, setCompanyValue] = useState<{
    importUsersAutomatically: boolean
    name: string
    announceNewUsers?: boolean
    userEndDate?: boolean
    hourlyLeaveAccounting?: boolean
    contactEmail: string
    shouldProrate?: boolean
    proratedLeaveTypes?: string[]
    shouldImportGuestUsers?: boolean
    defaultDepartmentForAutomaticImport?: string
    defaultLocationForAutomaticImport?: string
    calculationStartYear: Dayjs | string | null
  }>({
    importUsersAutomatically: authCompany?.importUsersAutomatically || false,
    userEndDate: Boolean(authCompany?.userEndDate),
    announceNewUsers: Boolean(authCompany?.announceNewUsers) || false,
    name: authCompany?.name || '',
    contactEmail: authCompany?.contactEmail || '',
    shouldProrate: Boolean(authCompany?.shouldProrate),
    proratedLeaveTypes: authCompany?.proratedLeaveTypes,
    shouldImportGuestUsers: authCompany?.shouldImportGuestUsers,
    defaultDepartmentForAutomaticImport: authCompany?.defaultDepartmentForAutomaticImport,
    defaultLocationForAutomaticImport: authCompany?.defaultLocationForAutomaticImport,
    calculationStartYear: authCompany?.calculationStartYear || dayjs(),
  })
  const [isLoadingTotalNumberOfUsers, setIsLoadingTotalNumberOfUsers] = useState(false)

  const [isLoading, setIsLoading] = useState(true)
  const [correlationId, setCorrelationId] = useState<string | null>(null)
  // The form store is the only source of truth: fetchCompany() rewrites the field on every refresh,
  // so a parallel state copy ends up disagreeing with what the radio shows and what onFinish submits.
  const importUsersAutomatically = Form.useWatch('importUsersAutomatically', form) ?? authCompany?.importUsersAutomatically ?? false
  const [showMsLoginRequiredModal, setShowMsLoginRequiredModal] = useState(false)
  const [adminConsentUrl, setAdminConsentUrl] = useState<null | string>(null)
  const [isMsAdminConsentRequired, setMsAdminConsentRequired] = useState(false)
  const [showAdminConsentWarning, setShowAdminConsentWarning] = useState(false)
  const [showSlackLoginRequiredModal, setShowSlackLoginRequiredModal] = useState(false)
  const [welcomeMessageOpts, setWelcomeMessageOpts] = useState<WelcomeMessageOptions[]>(getWelcomeMessageOptionsDefault(formatMessage))
  const [shouldShowAnnuonce, setShouldShowAnnouncement] = useState(!isMicrosoftBillingPayment)
  const [shouldShowDepartment, setShouldShowDepartment] = useState(false)
  const [shouldShowLocation, setShouldShowLocation] = useState(false)
  const [shouldShowProrate, setShouldShowProrate] = useState(false)
  const [selectedDepartment, setSelectedDepartment] = useState<IGetTeamsShort | null>(null)
  const [selectedLocation, setSelectedLocation] = useState<IGetLocationsShort | null>(null)
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState<ISelectedLeaveType[]>([])
  const [platformToSwitch, setPlatformToSwitch] = useState<(typeof allPlatforms)[number] | null>(null)
  const [showSwitchPlatformModal, setShowSwitchPlatformModal] = useState(false)
  const [showGoogleLoginRequiredModal, setShowGoogleLoginRequiredModal] = useState(false)
  const [showGoogleInsufficientPermissionsModal, setShowGoogleInsufficientPermissionsModal] = useState(false)
  const [shouldEnableImportUsersAutomaticallyForLargeCompanies, setShouldEnableImportUsersAutomaticallyForLargeCompanies] = useState(true)
  const [showEndDateInfoMessage, setShowEndDateInfoMessage] = useState(false)
  const [showMigrationModal, setShowMigrationModal] = useState(false)
  const dispatch = useAppDispatch()

  const [getCompanyGeneralQuery] = useManualQuery<IGetCompanyGeneral>(getCompanyGeneral)
  const [getGoogleScopesQuery] = useManualQuery<IGetGoogleScopes, { accessToken: string }>(getGoogleScopes)

  useEffect(() => {
    if (authCompany?.platform === 'email') {
      setShouldShowDepartment(false)
      setShouldShowLocation(false)
      setShouldShowAnnouncement(false)
    }

    fetchCompany()
  }, [authCompany?.platform, authCompany?.shouldImportGuestUsers])

  useEffect(() => {
    if (Array.isArray(actionNotifications) && correlationId && !actionNotifications.includes(correlationId || '')) {
      setCorrelationId(null)
      fetchCompany()
    }
  }, [actionNotifications])

  useEffect(() => {
    if (msAuth) {
      const tenantId = msAuth.getTenantId() || 'common'
      setAdminConsentUrl(`https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${process.env.REACT_APP_MICROSOFT_CLIENT_ID}`)
    }
  }, [msAuth])

  useEffect(() => {
    const importUsersFlag = featureFlags && featureFlags.includes(FeatureFlagEnum.importUsers)
    if (totalNumberOfUsers > AUTOMATIC_USERS_IMPORT_LIMIT) {
      setShouldEnableImportUsersAutomaticallyForLargeCompanies(importUsersFlag)
      setShouldShowDepartment(importUsersFlag)
      setShouldShowLocation(importUsersFlag)
      setShouldShowProrate(importUsersFlag)
    }
  }, [featureFlags, totalNumberOfUsers, isLoadingTotalNumberOfUsers])

  useEffect(() => {
    if (!shouldEnableImportUsersAutomaticallyForLargeCompanies) {
      notification.error({
        message: '',
        description: formatMessage({ id: 'components.importUsersForm.importUsersAutomaticallyExceedingLimit' }),
        duration: 0,
        btn: (
          <Button
            onClick={() => {
              openSupportChat()
            }}
          >
            {formatMessage({ id: 'app.contactSupport' })}
          </Button>
        ),
      })
      form.setFieldsValue({ importUsersAutomatically: false })
      setShouldShowDepartment(false)
      setShouldShowLocation(false)
      setShouldShowProrate(false)
    }
  }, [shouldEnableImportUsersAutomaticallyForLargeCompanies])

  const handleAutoImport = (value: boolean) => {
    form.setFieldsValue({ importUsersAutomatically: value })
    setShouldShowDepartment(value)
    setShouldShowLocation(value)
    setShouldShowProrate(value)
  }

  const { isValidatingGooglePermissions, adminTokenHash, pendingAutoImportValidation, setPendingAutoImportValidation, checkAutoImportAutomaticallyGoogle } =
    useGoogleAutoImportValidation({
      setShowGoogleLoginRequiredModal,
      setShowGoogleInsufficientPermissionsModal,
      onValidationFail: () => handleAutoImport(false),
      onUnexpectedError: (error: Error) => {
        notification.error({
          message: formatMessage({ id: 'error.google.validationFailed' }),
          description: error.message || 'An unexpected error occurred',
          duration: 0,
        })
      },
    })

  // Reading follows the saved value alone, so toggling the form does not look like provisioning started
  const hasSavedGoogleAutoImport = authCompany?.platform === 'google' && Boolean(authCompany?.importUsersAutomatically)
  // Saved as on and still on in the form: an unsaved toggle has no channels to show either way
  const showGoogleDirectoryConnection = hasSavedGoogleAutoImport && importUsersAutomatically

  const {
    channels: googleChannels,
    isRefreshing: isRefreshingGoogleChannels,
    refresh: refreshGoogleChannels,
  } = useGoogleDirectoryConnection({
    enabled: hasSavedGoogleAutoImport,
    onTokenExpired: () => setShowGoogleLoginRequiredModal(true),
    onRefreshSuccess: () => {
      notification.success({
        message: formatMessage({ id: 'googleConnection.refreshSuccess' }),
      })
      track('GOOGLE_DIRECTORY_CONNECTION_REFRESHED', { source: 'general settings page' })
    },
    onRefreshError: () => {
      notification.error({
        message: formatMessage({ id: 'googleConnection.refreshError' }),
      })
    },
  })

  const getGoogleAdminExtraPermissionsLink = () => {
    const hash = sessionStorage.getItem('adminTokenHash')
    return `https://app.vacationtracker.io/vt-google-permissions#${hash}`
  }

  const onImportUsersAutomaticallySelect = async (value: boolean): Promise<void> => {
    // Dont call total users if user turn off auto import
    if (authCompany?.platform === 'microsoft' && value) {
      getTotalUsersInMicrosoftTenant()
    }

    if (authCompany?.platform === 'slack') {
      fetchFromSlackApi(isBoolean(authCompany?.shouldImportGuestUsers) ? authCompany?.shouldImportGuestUsers : true)
    }

    // Nothing to count when switching off, and the fetch only put the section behind a spinner
    if (authCompany?.platform === 'google' && value) {
      const isValid = await checkAutoImportAutomaticallyGoogle(value)
      if (!isValid) {
        return // Validation failed, don't proceed
      }
      fetchFromGoogleApi()
    }

    handleAutoImport(Boolean(value))

    track('IMPORT_USERS_AUTOMATICALLY', {
      platform: authCompany?.platform,
      selectedValue: value,
      source: 'general settings page',
    })
  }

  const hasMicrosoftPermissionsForAutoImport = (): boolean => {
    if (authCompany?.platform !== 'microsoft') {
      return true
    }
    const tokensExpiration = sessionStorage.getItem('msWebAppTokensExpiration')
    if (!tokensExpiration || isAfter(new Date(), new Date(tokensExpiration))) {
      return false
    }
    const accessToken = sessionStorage.getItem('msWebAppTokensAccessToken') as string
    return msAuth.getPermissions(accessToken).toLowerCase().includes('group.read.all')
  }

  const shouldDisableSave = () => {
    const { name, importUsersAutomatically, sendWelcomeMessageTo, userEndDate, hourlyLeaveAccounting, shouldProrate, shouldImportGuestUsers, calculationStartYear, contactEmail } =
      form.getFieldsValue(true)
    if (isLoading || isLoadingTotalNumberOfUsers) {
      return true
    }

    const calculationYearCheck = isNull(authCompany?.calculationStartYear) ? true : authCompany?.calculationStartYear === calculationStartYear?.format('YYYY')

    const departmentCheck = isNull(authCompany?.defaultDepartmentForAutomaticImport) ? true : authCompany?.defaultDepartmentForAutomaticImport === selectedDepartment?.id

    const locationCheck = isNull(authCompany?.defaultLocationForAutomaticImport) ? true : authCompany?.defaultLocationForAutomaticImport === selectedLocation?.id

    const announceCheck = authCompany?.platform === 'email' ? true : authCompany?.announceNewUsers === (sendWelcomeMessageTo?.platform === authCompany?.platform)

    const guestUsersCheck = authCompany?.platform === 'slack' ? Boolean(authCompany?.shouldImportGuestUsers) === shouldImportGuestUsers : true

    const contactEmailCheck = authCompany?.contactEmail === contactEmail

    const simpleComparisonCheck =
      authCompany?.name === name &&
      authCompany?.importUsersAutomatically === importUsersAutomatically &&
      Boolean(authCompany?.shouldProrate) === shouldProrate &&
      authCompany?.userEndDate === userEndDate &&
      authCompany?.hourlyLeaveAccounting === hourlyLeaveAccounting &&
      announceCheck &&
      guestUsersCheck &&
      departmentCheck &&
      locationCheck &&
      calculationYearCheck &&
      contactEmailCheck

    const proratedLeaveTypesCheck = authCompany?.shouldProrate ? isEqual(sortBy(authCompany?.proratedLeaveTypes), sortBy(selectedLeaveTypes?.map((lt) => lt.id))) : true

    return simpleComparisonCheck && proratedLeaveTypesCheck
  }

  const fetchCompany = async () => {
    try {
      const response = await getCompanyGeneralQuery()
      if (response.error || !response.data) throw response.error
      if (response.data.getCompany?.platform === 'microsoft') {
        const tenantId = await Api.get<string>('/microsoft/get-tenant')
        setMsTenantId(tenantId)
      }
      const companyData = response.data.getCompany
      setShouldDisableDailyTracking(Boolean(response.data.getWorkWeekList?.some((workWeek) => workWeek.schedule || userHasWorkingHoursSchedule(workWeek.workHours))))
      if (companyData) {
        form.setFieldsValue({
          importUsersAutomatically: companyData.importUsersAutomatically,
          sendWelcomeMessageTo: companyData?.announceNewUsers
            ? (welcomeMessageOpts.find((opt) => opt.platform === authCompany?.platform) as WelcomeMessageOptions)
            : getDontSendWelcomeOption(formatMessage),
          userEndDate: companyData.userEndDate,
          name: companyData.name,
          contactEmail: companyData.contactEmail,
          hourlyLeaveAccounting: Boolean(companyData.hourlyLeaveAccounting),
          shouldImportGuestUsers: Boolean(companyData.shouldImportGuestUsers),
          shouldProrate: Boolean(companyData.shouldProrate),
          proratedLeaveTypes: companyData.proratedLeaveTypes,
          defaultDepartmentForAutomaticImport: companyData.defaultDepartmentForAutomaticImport,
          defaultLocationForAutomaticImport: companyData.defaultLocationForAutomaticImport,
          calculationStartYear: companyData.calculationStartYear ? dayjs(companyData.calculationStartYear as string) : dayjs(),
        })
        setCompanyValue({
          importUsersAutomatically: companyData.importUsersAutomatically || false,
          announceNewUsers: Boolean(companyData.announceNewUsers) || false,
          userEndDate: companyData.userEndDate || false,
          name: companyData.name,
          contactEmail: companyData.contactEmail,
          hourlyLeaveAccounting: Boolean(companyData.hourlyLeaveAccounting),
          shouldImportGuestUsers: Boolean(companyData.shouldImportGuestUsers),
          shouldProrate: Boolean(companyData.shouldProrate),
          proratedLeaveTypes: companyData.proratedLeaveTypes,
          defaultDepartmentForAutomaticImport: companyData.defaultDepartmentForAutomaticImport,
          defaultLocationForAutomaticImport: companyData.defaultLocationForAutomaticImport,
          calculationStartYear: companyData.calculationStartYear || null,
        })
      }
      if (authCompany) {
        dispatch(
          setAuthCompany({
            ...authCompany,
            importUsersAutomatically: companyData.importUsersAutomatically,
            announceNewUsers: Boolean(companyData.announceNewUsers),
            userEndDate: companyData.userEndDate,
            name: companyData.name,
            contactEmail: companyData.contactEmail,
            hourlyLeaveAccounting: Boolean(companyData.hourlyLeaveAccounting),
            shouldImportGuestUsers: Boolean(companyData.shouldImportGuestUsers),
            shouldProrate: Boolean(companyData.shouldProrate),
            proratedLeaveTypes: companyData.proratedLeaveTypes,
            defaultDepartmentForAutomaticImport: companyData.defaultDepartmentForAutomaticImport,
            defaultLocationForAutomaticImport: companyData.defaultLocationForAutomaticImport,
            calculationStartYear: companyData.calculationStartYear,
          })
        )
      }
      setShouldShowDepartment(Boolean(companyData.importUsersAutomatically))
      setShouldShowLocation(Boolean(companyData.importUsersAutomatically))
      setShouldShowProrate(Boolean(companyData.importUsersAutomatically))
      setIsLoading(false)
    } catch (err) {
      setIsLoading(false)
      setSubmitLoader(false)
    }
  }

  const disableDate = (current) => {
    return !validateCalculationStartYear(current.format('YYYY'))
  }

  const onFinish = async () => {
    try {
      const values: ICompanySettingsFormValues = await form.validateFields()
      if (authCompany?.platform === 'microsoft' && values.importUsersAutomatically && isMsAdminConsentRequired && adminConsentUrl) {
        return setShowAdminConsentWarning(true)
      }

      if (authCompany?.platform === 'microsoft' && authCompany.importUsersAutomatically !== values.importUsersAutomatically && values.importUsersAutomatically) {
        if (!sessionStorage.getItem('msWebAppTokensExpiration') || isAfter(new Date(), new Date(sessionStorage.getItem('msWebAppTokensExpiration') as string))) {
          return setShowMsLoginRequiredModal(true)
        }
      }

      if (authCompany?.platform === 'google' && authCompany.importUsersAutomatically !== values.importUsersAutomatically) {
        const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
        if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration)) || !sessionStorage.getItem('googleRefreshToken')) {
          return setShowGoogleLoginRequiredModal(true)
        }
      }

      setShowAdminConsentWarning(false)

      if (typeof values.userEndDate !== 'boolean') {
        values.userEndDate = false
      }

      if (values.calculationStartYear) {
        const safeCalculationStartYear = dayjs(values.calculationStartYear as string).add(1, 'month')
        values.calculationStartYear = safeCalculationStartYear.format('YYYY')
      }

      const body: ICompanySettingsBody = {
        eventType: 'COMPANY_SETTINGS_UPDATED',
        eventGroup: 'COMPANY',
        ...omit(values, ['sendWelcomeMessageTo', 'selectedLocation', 'selectedLocation', 'selectedDepartment', 'selectedLeaveTypes']),
        importUsersAutomatically: Boolean(importUsersAutomatically),
        announceNewUsers: values?.sendWelcomeMessageTo?.platform !== 'none',
        defaultLocation: values?.selectedLocation,
        defaultDepartment: values?.selectedDepartment,
        proratedLeaveTypes: values?.selectedLeaveTypes,
      }

      if (authCompany?.platform === 'slack') {
        body.shouldImportGuestUsers = isBoolean(values.shouldImportGuestUsers) ? values.shouldImportGuestUsers : true
      }

      if (importUsersAutomatically && values.shouldProrate && values?.selectedLeaveTypes?.length) {
        body.shouldProrate = true
        body.proratedLeaveTypes = values.selectedLeaveTypes
      } else {
        body.shouldProrate = false
        body.proratedLeaveTypes = []
      }

      if (authCompany?.platform === 'microsoft') {
        const refreshToken = sessionStorage.getItem('msWebAppTokensRefreshToken')
        if (refreshToken) {
          body.refreshToken = refreshToken as string
        }
      }

      // Auto-import for Google Workspace will not work if the user has the GOOGLE_READ_USER_PERMISSION scope but is not a super admin on the Google Workspace account.
      // We need to come up with a way to handle this.
      if (authCompany?.platform === 'google' && authCompany.importUsersAutomatically !== values.importUsersAutomatically && values.importUsersAutomatically) {
        const refreshToken = sessionStorage.getItem('googleRefreshToken')
        if (refreshToken) {
          body.refreshToken = refreshToken as string
        }
      }
      setSubmitLoader(true)

      if (authCompany?.platform === 'email') {
        delete body.announceNewUsers
        delete body.importUsersAutomatically
        delete body.shouldProrate
        delete body.refreshToken
        delete body.proratedLeaveTypes
        delete body.defaultDepartment
        delete body.defaultLocation
        delete body.shouldImportGuestUsers
      }

      if (!shouldEnableImportUsersAutomaticallyForLargeCompanies) {
        delete body.shouldImportGuestUsers
        delete body.importUsersAutomatically
        delete body.shouldProrate
        delete body.proratedLeaveTypes
        delete body.defaultDepartment
        delete body.defaultLocation
        delete body.refreshToken
      }

      const response = await Api.post('/core/event', body)
      setCorrelationId(response.correlationId as string)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'general.updateInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

      setSubmitLoader(false)
    } catch (error) {
      logger.error(error)
    }
  }

  const fetchFromSlackApi = async (shouldImportGuestUsers = true, from?: 'guestUsers'): Promise<void> => {
    if (from !== 'guestUsers') {
      setIsLoadingTotalNumberOfUsers(true)
    }
    try {
      slackAuth.getUserToken()
      let slackUsers: ISlackUser[] = []
      try {
        slackAuth.getUserToken()
        slackUsers = await slackAuth.getUsersList()
      } catch (error) {
        logger.error(error)
        if (error?.error === 'not_authed' || error?.error === 'invalid_auth' || error?.error === 'missing_scope' || error?.error === 'token_revoked') {
          setShowSlackLoginRequiredModal(true)
        }
        return
      }
      const activeUsers = slackUsers
        .filter((user) => !user.deleted)
        .filter((user) => !user.is_bot)
        .filter((user) => !user.is_ultra_restricted)
        .filter((user) => user.id !== 'USLACKBOT')

      if (shouldImportGuestUsers) {
        setTotalNumberOfUsers(activeUsers.filter((user) => !user.is_restricted).length)
      } else {
        setTotalNumberOfUsers(activeUsers.length)
      }

      setIsLoadingTotalNumberOfUsers(false)
      try {
        await Api.get('/slack/get-bot-token')
      } catch (error) {
        if (error.response?.data?.error && (error.response.data.error === 'error.botTokenNotExist' || error.response.data.error === 'error.botTokenNotValid')) {
          setShowSlackLoginRequiredModal(true)
          setIsLoadingTotalNumberOfUsers(false)
          return
        }
      }
    } catch (error) {
      logger.error('ERROR FETCH FROM SLACK API', error)
      setIsLoadingTotalNumberOfUsers(false)
    }
  }

  const getGoogleTokens = (withConsent = false) => {
    googleAuth.signin(withConsent).then(async () => {
      // Close the login modal after successful OAuth
      setShowGoogleLoginRequiredModal(false)
      // If user was trying to enable auto-import when login was required, re-run validation
      if (pendingAutoImportValidation) {
        setPendingAutoImportValidation(false)
        // The failed attempt flipped the switch back to No. Go through the normal enable path so the
        // directory count is fetched and an oversized Workspace can still be turned away.
        await onImportUsersAutomaticallySelect(true)
        return
      }
      fetchFromGoogleApi()
    })
  }

  const fetchFromGoogleApi = async (): Promise<void> => {
    if (!authCompany) {
      throw new Error('Missing company')
    }
    setIsLoadingTotalNumberOfUsers(true)
    try {
      const googleTokenExpiration = sessionStorage.getItem('googleTokenExpiration')
      if (!googleTokenExpiration || isAfter(new Date(), new Date(googleTokenExpiration))) {
        setShowGoogleLoginRequiredModal(true)
        return
      }
      setShowGoogleLoginRequiredModal(false)
      const googleScopesResponse = await getGoogleScopesQuery({
        variables: {
          accessToken: googleAuth.getAccessToken() as string,
        },
      })
      if (googleScopesResponse.error || !googleScopesResponse.data) throw googleScopesResponse.error
      const googleApiScopes = googleScopesResponse.data.getGoogleTokenInfo.scopes as string[]
      const vtGooogleIntegrationScopes = googleScopesResponse.data.getGoogleIntegrationSettings?.scopes
      if (googleApiScopes?.includes(GOOGLE_READ_USER_PERMISSION) && !vtGooogleIntegrationScopes?.includes(GOOGLE_READ_USER_PERMISSION)) {
        const userInfo = googleAuth.getSignedInUser()
        await Api.post('/core/event', {
          eventType: 'GOOGLE_INTEGRATION',
          eventGroup: 'INTEGRATION',
          scope: googleApiScopes.join(' '),
          domainName: userInfo.hd,
        })
      }

      try {
        const googleUsers = await googleAuth.getUserListForImportFull()

        setTotalNumberOfUsers((googleUsers.length as number) || 0)
      } catch (error) {
        if (error.message === 'GOOGLE_DIRECTORY_SHARING_DISABLED' || error.message === 'GOOGLE_DIRECTORY_PERMISSION_DENIED') {
          track('GOOGLE_ERROR_DIRECTORY_SHARING_DISABLED', { page: 'ImportUsersPage', platform: 'google', error: error.message })
          setShowEnableGoogleApiModal(true)
          return
        }
        if (error.message === 'no_tokens') {
          errorNotificationHandler(error, 'google', getGoogleTokens)
          return
        }
        notification.error({
          message: `Google error: ${error}`,
          description: 'Please logout and login again',
          duration: 0,
        })
        Sentry.captureException(error)
      }
    } finally {
      // Every early return above used to leave the section stuck behind its loading flag
      setIsLoadingTotalNumberOfUsers(false)
    }
  }

  const wrongSlackAccount = () => {
    slackAuth.removeTokens()
    notification.error({
      message: formatMessage({ id: 'error.slack.wrongAccount' }),
      description: (
        <div>
          <Paragraph>{formatMessage({ id: 'errors.slack.wrongSlackAccountDescription' })}</Paragraph>
          <Button onClick={() => getSlackTokens()}>{formatMessage({ id: 'error.slack.logInWithDifferentAccount' })}</Button>
        </div>
      ),
      duration: 0,
    })
    setShowSlackLoginRequiredModal(false)
  }

  const getSlackTokens = () => {
    slackAuth
      .signin([...SLACK_REQUIRED_BOT_SCOPES, ...(authCompany?.aiBotHelper ? ['im:history'] : [])], SLACK_REQUIRED_USER_SCOPES, authCompany?.organizationId)
      .then(async (response) => {
        if ((!response.enterprise && response.team.id !== authCompany?.organizationId) || (response.enterprise && response.enterprise?.id !== authCompany?.organizationId)) {
          wrongSlackAccount()
          return
        }
        slackAuth.setUserToken(response.authed_user.access_token)
        try {
          await Api.post('/slack/update-tokens', {
            token: response.authed_user.access_token,
            botToken: response.access_token,
          })
        } catch (error) {
          logger.error('UPDATE TOKEN ERROR', error.response)
        }
        setShowSlackLoginRequiredModal(false)
        await fetchFromSlackApi(isBoolean(authCompany?.shouldImportGuestUsers) ? authCompany?.shouldImportGuestUsers : true)
      })
      .catch((error) => {
        logger.error('SLACK SIGNIN ERROR', error)
        errorNotificationHandler(error, 'slack', () => {
          fetchFromSlackApi(isBoolean(authCompany?.shouldImportGuestUsers) ? authCompany?.shouldImportGuestUsers : true)
        })
      })
  }

  const getMsTokensForUserList = async (tenantId: string) => {
    try {
      await msAuth.getMSWebAppTokens(tenantId, addMinutes(new Date(), 15).toString(), 'offline_access user.read group.read.all user.readbasic.all team.readbasic.all')
      setShowMsLoginRequiredModal(false)
      getTotalUsersInMicrosoftTenant()
    } catch (error) {
      setShowMsLoginRequiredModal(false)
      setMsAdminConsentRequired(true)
    }
  }

  const getTotalUsersInMicrosoftTenant = async () => {
    setIsLoadingTotalNumberOfUsers(true)
    try {
      const tenantId = await Api.get<string>('/microsoft/get-tenant')
      setMsTenantId(tenantId)

      const teamId = authCompany?.msTeamId && authCompany?.msTeamId !== tenantId ? authCompany?.msTeamId : null

      if (!sessionStorage.getItem('msWebAppTokensExpiration') || isAfter(new Date(), new Date(sessionStorage.getItem('msWebAppTokensExpiration') as string))) {
        setShowMsLoginRequiredModal(true)
      } else {
        const msWebAppTokensAccessToken = sessionStorage.getItem('msWebAppTokensAccessToken') as string
        const scopes = msAuth.getPermissions(msWebAppTokensAccessToken)
        if (form.getFieldValue('importUsersAutomatically') && !scopes.toLowerCase().includes('group.read.all')) {
          setIsLoadingTotalNumberOfUsers(false)
          return setShowMsLoginRequiredModal(true)
        }
        const numberOfUsers = await msAuth.getTotalUsers(msWebAppTokensAccessToken, teamId)
        setTotalNumberOfUsers(numberOfUsers)
      }
      setIsLoadingTotalNumberOfUsers(false)
    } catch (err) {
      if (
        err?.data?.error?.code === 'InvalidAuthenticationToken' ||
        err?.includes('FAILED CODE OR TOKEN FETCH') ||
        err?.includes('invalid_grant') ||
        (err?.includes('invalid_request') && err?.includes('refresh_token'))
      ) {
        return setShowMsLoginRequiredModal(true)
      }

      captureException(err)
      errorNotificationHandler(err, 'microsoft', () => {
        getTotalUsersInMicrosoftTenant()
      })
      setMsAdminConsentRequired(true)
      setIsLoadingTotalNumberOfUsers(false)
    }
  }

  const errorNotificationHandler = (error: Error, platform: Platform, onClickHandler: Function) => {
    const isTokenError = error.message === 'no_tokens'
    const errorMessageDescription = isTokenError ? formatMessage({ id: 'error.token.description' }, { platform }) : error.message
    notification.error({
      message: formatMessage({ id: 'error.generic' }),
      description: errorMessageDescription,
      key: 'error-notification-handler',
      btn: isTokenError && (
        <Button
          onClick={() => {
            onClickHandler()
            notification.destroy('error-notification-handler')
          }}
        >
          {formatMessage({ id: 'error.token.button' }, { platform })}
        </Button>
      ),
      duration: 0,
    })
  }

  const handleSwitchPlatform = () => {
    setShowSwitchPlatformModal(!showSwitchPlatformModal)
  }

  const handleChangeMigrationFeature = () => {
    setShowMigrationModal(!showMigrationModal)
  }

  const handleChangeGuestUsersImport = async (v: boolean) => {
    await fetchFromSlackApi(v, 'guestUsers')
  }

  const saveSettings = async () => {
    try {
      const formData = await form.validateFields()

      if (selectedLeaveTypes?.length === 0 && authCompany?.platform !== 'email' && form.getFieldValue('shouldProrate')) {
        notification.error({
          message: formatMessage({ id: 'connect.platformErrorTitle' }),
          // eslint-disable-next-line max-len
          description: `Internal error. Selected leave types length (${selectedLeaveTypes?.length}) with prorate turned on and platform is ${authCompany?.platform}. Please contact support.`,
          duration: 0,
        })
        return
      }

      // TODO: We need to change this part from 783 to 797
      // If the admin on MS has given consent only for themselves, and not for other admins on VT who are not admins on MS,
      // those other admins won't be able to make any changes on the general page without consent.
      // START
      if (importUsersAutomatically && !hasMicrosoftPermissionsForAutoImport()) {
        setShowMsLoginRequiredModal(true)
        return
      }
      // END

      try {
        showConfirmCompanySettings(
          {
            onConfirm: () => {
              onFinish()
            },
            title: formatMessage({ id: 'general.confirmModalTitle' }),
            okText: formatMessage({ id: 'components.modal.confirm' }),
            formatMessage,
            showInfoFor: {
              showNewNameInfo: true,
              showImportUsersAutomaticallyInfo: true,
              showAiBotHelperInfo: false,
              showUserEndDateInfo: true,
              showHourlyLeaveAccountingInfo: true,
              showWelcomeMessageToInfo: Boolean(formData?.sendWelcomeMessageTo?.platform !== 'none') !== companyValue?.announceNewUsers,
              showSlackMultichannelUsersImport: Boolean(authCompany?.platform === 'slack') && importUsersAutomatically,
              showCalculationStartYearInfo: true,
              showAlwaysDefaultDepartmentAndLocation: false,
              showMoreInfo: false,
              shownNOfUsersToImportInfo: false,
              showCompanyEmail: formData?.contactEmail !== companyValue?.contactEmail,
            },
            showAdditionalBillingInfo: false,
            upcomingInvoiceInfo: null,
            isLoadingUpcomingInvoiceInfo: false,
            locale: authUser?.locale as LocaleEnum,
            welcomeMessageOpts,
            authCompany,
            selectedDepartment,
            selectedLocation,
            selectedLeaveTypes,
            form,
            contactEmail: form.getFieldValue('contactEmail'),
          },
          modal.confirm
        )
      } catch (error) {
        logger.error(error)
        Sentry.captureException(
          JSON.stringify({
            ...error,
            message: 'Error running showConfirmCompanySettings()',
            formData,
            authCompany,
            authUser,
            companyValue,
            importUsersAutomatically,
          })
        )
      }
      return
    } catch (error) {
      return
    }
  }

  return (
    <div className="main-content form-background">
      <PageHeader title={<IntlMessages id="app.general" />} />
      <div className="main-content-body">
        <div className="genereal-body form-layout">
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Form form={form} layout="vertical" initialValues={companyValue} requiredMark="optional" size="large" labelWrap>
              <Row
                gutter={{
                  xs: 8,
                  sm: 16,
                  md: 24,
                  lg: 32,
                }}
              >
                <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                  <Title level={4}>
                    <IntlMessages id="form.general.companySettingsTitle" />
                  </Title>
                  <Paragraph type="secondary">
                    <IntlMessages id="form.general.companySettingsDescription" />
                  </Paragraph>
                </Col>
                <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                  <Form.Item name="name" label={<IntlMessages id="general.companyName" />} rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                    <Input maxLength={100} placeholder={`${formatMessage({ id: 'general.pleaseEnterCompanyName' })}`} data-qa="company-name" />
                  </Form.Item>

                  <Form.Item
                    label={<IntlMessages id="createCompany.steps.companyForm.email" />}
                    name="contactEmail"
                    rules={[
                      {
                        required: true,
                        message: <IntlMessages id="createCompany.steps.companyForm.contactEmail.validation.required" />,
                      },
                      {
                        type: 'email',
                        message: <IntlMessages id="form.validEmailRequired" />,
                      },
                    ]}
                  >
                    <Input type="email" placeholder={`${formatMessage({ id: 'general.pleaseEnterContactEmail' })}`} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Row
                gutter={{
                  xs: 8,
                  sm: 16,
                  md: 24,
                  lg: 32,
                }}
              >
                <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                  <Title level={4}>
                    <IntlMessages id="form.general.configurationTitle" />
                  </Title>
                  <Paragraph type="secondary">
                    <IntlMessages id="form.general.configurationDescription" values={{ br: () => <br /> }} />
                  </Paragraph>
                </Col>
                <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                  <YesNoSwitch
                    name="userEndDate"
                    defaultValue={authCompany ? Boolean(authCompany.userEndDate) : undefined}
                    onChange={(value) => {
                      if (value && !authCompany?.userEndDate) {
                        setShowEndDateInfoMessage(true)
                      } else {
                        setShowEndDateInfoMessage(false)
                      }
                    }}
                    label={
                      <span>
                        <IntlMessages id="general.enableUserEndDate" />
                        &nbsp;
                        <Tooltip title={<IntlMessages id="general.enableUserEndDateTooltip" />} className="info-tooltip">
                          <InfoCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                  />

                  {showEndDateInfoMessage && <Alert message={<IntlMessages id="general.enableUserEndDateInfo" />} showIcon />}

                  <Form.Item
                    label={
                      <span>
                        <IntlMessages id="general.hourlyLeaveAccounting" />
                        {!shouldEnableHours && (
                          <>
                            &nbsp;
                            <Tooltip title={<IntlMessages id="general.hourlyLeaveAccountingTooltip" />} className="info-tooltip">
                              <InfoCircleOutlined />
                            </Tooltip>
                          </>
                        )}
                      </span>
                    }
                    name="hourlyLeaveAccounting"
                    required={true}
                  >
                    <Select<Boolean> style={{ width: 100 }} disabled={!shouldEnableHours}>
                      <Select.Option disabled={shouldDisableDailyTracking} value={false}>
                        <Tooltip title={shouldDisableDailyTracking ? <IntlMessages id="general.daysOptionDisabledTooltip" /> : undefined}>
                          <span>
                            <IntlMessages id="general.days" />
                          </span>
                        </Tooltip>
                      </Select.Option>
                      <Select.Option value={true}>
                        <IntlMessages id="general.hours" />
                      </Select.Option>
                    </Select>
                  </Form.Item>

                  {(!!companyValue?.calculationStartYear || shouldEnableCalculationStartYear) && (
                    <Form.Item
                      label={
                        <span>
                          <IntlMessages id="general.calculationStartYear" />
                          &nbsp;
                          <Tooltip className="info-tooltip" title={<IntlMessages id="general.calculationStartYearTooltip" />}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </span>
                      }
                      name="calculationStartYear"
                      rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                    >
                      <DatePicker disabledDate={disableDate} picker="year" />
                    </Form.Item>
                  )}
                  {authCompany?.platform !== 'email' && authCompany?.platform !== 'google' && (
                    <Paragraph type="secondary">
                      <IntlMessages id="components.generalSettings.aiBotHelperLabel" />
                      {' - '}
                      <Link to={FrontendUrls.featuresPermissions}>
                        <IntlMessages id="components.featuresPermissions.movedToFeaturesPermissions" />
                      </Link>
                    </Paragraph>
                  )}
                </Col>
              </Row>

              <Divider />

              {shouldShowMigrationSection(authUser.role) && (
                <>
                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                      <Title level={4}>
                        <IntlMessages id="form.general.migrationFeatureTitle" />
                      </Title>
                      <Paragraph type="secondary">
                        <IntlMessages id="form.general.migrationFeatureDescription" />
                      </Paragraph>
                    </Col>
                    <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                      <Form.Item
                        label={
                          <span>
                            <IntlMessages id="general.clickToSelectPlatform" />
                            &nbsp;
                          </span>
                        }
                      >
                        <Row gutter={[16, 16]}>
                          {allPlatforms
                            .reduce((rows: any[][], platform: string, index: number) => {
                              if (index % 2 === 0) {
                                rows.push([platform])
                              } else {
                                rows[rows.length - 1].push(platform)
                              }
                              return rows
                            }, [])
                            .map((row, rowIndex) => (
                              <React.Fragment key={rowIndex}>
                                {row.map((val) => (
                                  <Col span={12} key={val}>
                                    <Tooltip title={authCompany?.companyMigrated ? formatMessage({ id: 'general.companyAlreadyMigrated' }) : null}>
                                      <Button
                                        key={val}
                                        icon={platformIconMap[val]}
                                        type="primary"
                                        onClick={() => {
                                          setPlatformToSwitch(val)
                                          handleChangeMigrationFeature()
                                        }}
                                        disabled={
                                          !MIGRATION_AVAILABLE_PLATFORMS.includes(val) || (val === 'email' && authCompany?.platform === 'email') || authCompany?.companyMigrated
                                        }
                                        style={{ width: '100%' }}
                                      >
                                        {val === authCompany?.platform && val !== 'email' ? (
                                          <IntlMessages id="general.switchTenant" values={{ platform: capitalize(val) }} />
                                        ) : (
                                          <IntlMessages id="general.connectWith" values={{ platform: capitalize(val) }} />
                                        )}
                                      </Button>
                                    </Tooltip>
                                  </Col>
                                ))}
                              </React.Fragment>
                            ))}
                        </Row>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Divider />
                </>
              )}
              <>
                {authCompany?.platform !== 'email' && (
                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                      <Title level={4}>
                        <IntlMessages id="general.importUsersConfigSection" />
                      </Title>
                      <Paragraph type="secondary">
                        <IntlMessages id="general.importUsersConfigSectionDescription" values={{ br: () => <br /> }} />
                      </Paragraph>
                    </Col>
                    <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                      {!shouldEnableImportUsersAutomaticallyForLargeCompanies && (
                        <Alert type="warning" message={<IntlMessages id="components.importUsersForm.importUsersAutomaticallyExceedingLimit" />} />
                      )}
                      <YesNoSwitch
                        name="importUsersAutomatically"
                        onChange={onImportUsersAutomaticallySelect}
                        loading={isLoadingTotalNumberOfUsers || (isValidatingGooglePermissions && authCompany?.platform === 'google')}
                        label={
                          <IntlMessages
                            id="components.importUsersForm.button.importUsersAutmaticallyFromPlatform"
                            values={{
                              platform: getIntegrationPlatformName(authCompany?.platform as Platform),
                            }}
                          />
                        }
                      />

                      {showGoogleDirectoryConnection && (
                        <>
                          <GoogleDirectoryConnection channels={googleChannels} isRefreshing={isRefreshingGoogleChannels} onRefresh={refreshGoogleChannels} />
                          <Divider />
                        </>
                      )}

                      {showAdminConsentWarning && (
                        <Text type="danger">
                          <WarningOutlined /> <IntlMessages id="components.importUsersForm.consent.warning" />
                        </Text>
                      )}
                      <ConfigureImportUsersOptionsForm
                        form={form}
                        source={ConfigureImportSourceEnum.generalSettingsPage}
                        welcomeMessageOpts={welcomeMessageOpts}
                        onChangeGuestUsersImport={handleChangeGuestUsersImport}
                        setSelectedDepartment={setSelectedDepartment}
                        setSelectedLocation={setSelectedLocation}
                        setSelectedLeaveTypes={setSelectedLeaveTypes}
                        setWelcomeMessageOpts={setWelcomeMessageOpts}
                        shouldShowAnnounce={shouldShowAnnuonce}
                        shouldShowDepartment={shouldShowDepartment}
                        shouldShowLocation={shouldShowLocation}
                        shouldShowProrate={shouldShowProrate}
                        shouldShowImportGuestUsers={importUsersAutomatically && shouldEnableImportUsersAutomaticallyForLargeCompanies}
                        formLayout={{
                          labelCol: { span: 24 },
                          wrapperCol: { span: 24 },
                        }}
                      />
                    </Col>
                  </Row>
                )}

                <Row
                  gutter={{
                    xs: 8,
                    sm: 16,
                    md: 24,
                    lg: 32,
                  }}
                >
                  <Col span={24}>
                    <Form.Item style={{ textAlign: 'right' }} shouldUpdate>
                      {() => {
                        //see PR-1808 description
                        return (
                          <Button
                            className="button-right"
                            type="primary"
                            loading={submitLoader}
                            disabled={shouldDisableSave()}
                            data-qa="save-settings"
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onClick={saveSettings}
                          >
                            <IntlMessages id="app.save" />
                          </Button>
                        )
                      }}
                    </Form.Item>
                  </Col>
                </Row>
              </>
            </Form>
          )}
          {showSwitchPlatformModal && <PlatformSwitchModal visible={showSwitchPlatformModal} onCancel={handleSwitchPlatform} platform={platformToSwitch || ''} />}
          {showMigrationModal && authCompany?.platform && platformToSwitch && (
            <MigrationFeatureModal
              organizationId={authCompany.organizationId}
              visible={showMigrationModal}
              onCancel={() => setShowMigrationModal(false)}
              toPlatform={platformToSwitch}
              fromPlatform={authCompany?.platform}
            />
          )}
        </div>

        <Divider />

        <Text style={{ display: 'block', marginBottom: '20px' }}>
          <IntlMessages
            id="general.deleteAccount"
            values={{
              mail: (...chunks) => <a href="mailto:hello@vacationtracker.io">{chunks}</a>,
            }}
          />
        </Text>
        <Text type="secondary">
          <IntlMessages id="general.usefulLinksTitle" />
        </Text>
        <IntlMessages
          id="general.usefulLinks"
          values={{
            // eslint-disable-next-line
            healthcheck: (...chunks) => {
              return <a href="https://app.vacationtracker.io/health-check/">{chunks}</a>
            },
            // eslint-disable-next-line
            helpdesk: (...chunks) => (
              <a href="https://vacationtracker.io/knowledge-base/" target="_blank" rel="noopener noreferrer">
                {chunks}
              </a>
            ),
          }}
        />
        <br />
        <Text type="secondary" copyable={{ text: authCompany?.id }}>
          <IntlMessages id="general.companyId" /> {authCompany?.id}
        </Text>
      </div>

      {showMsLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.microsoft.notificationsLoginRequiredTitle' })}
          open={showMsLoginRequiredModal}
          onOk={() => {
            getMsTokensForUserList(msTenantId)
          }}
          onCancel={() => {
            setShowMsLoginRequiredModal(false)
            history.push(FrontendUrls.home)
          }}
        >
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="microsoft.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showSlackLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.slack.usersLoginRequiredTitle' })}
          open={showSlackLoginRequiredModal}
          onOk={getSlackTokens}
          onCancel={() => {
            setShowSlackLoginRequiredModal(false)
          }}
        >
          <p>
            <IntlMessages id="slack.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="slack.notificationsLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showGoogleLoginRequiredModal && (
        <Modal
          title={formatMessage({ id: 'error.google.loginRequiredTitle' })}
          open={showGoogleLoginRequiredModal}
          onOk={() => getGoogleTokens(true)}
          onCancel={() => {
            setShowGoogleLoginRequiredModal(false)
          }}
          okButtonProps={{ disabled: isValidatingGooglePermissions }}
        >
          <p>
            <IntlMessages id="error.google.usersLoginRequiredDescription1" />
          </p>
          <p>
            <IntlMessages id="error.google.usersLoginRequiredDescription2" />
          </p>
        </Modal>
      )}
      {showEnableGoogleApiModal && (
        <Modal title={formatMessage({ id: 'error.google.directoryApiDisabledTitle' })} open={showEnableGoogleApiModal} footer={null}>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP1" />
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP2" />
          </Paragraph>
          <Paragraph copyable={{ text: supportLink }}>
            <a href={supportLink} target="_blank" rel="noopener noreferrer">
              {supportLink}
            </a>
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.directoryApiDisabledP4" />
          </Paragraph>
        </Modal>
      )}
      {showGoogleInsufficientPermissionsModal && (
        <Modal
          title={formatMessage({ id: 'error.google.insufficientAdminPermissionsTitle' })}
          open={showGoogleInsufficientPermissionsModal}
          footer={null}
          onCancel={() => {
            setShowGoogleInsufficientPermissionsModal(false)
          }}
        >
          <Paragraph>
            <IntlMessages id="error.google.insufficientAdminPermissionsDescription" />
          </Paragraph>
          <Paragraph>
            <IntlMessages id="error.google.superAdminRequired" />
          </Paragraph>
          {adminTokenHash && (
            <>
              <Paragraph>
                <IntlMessages id="connect.google.sharedPermissionsRequiredDescription" />
              </Paragraph>
              <Paragraph copyable={{ text: getGoogleAdminExtraPermissionsLink() }}>
                <a href={getGoogleAdminExtraPermissionsLink()} target="_blank" rel="noopener noreferrer">
                  {getGoogleAdminExtraPermissionsLink()}
                </a>
              </Paragraph>
            </>
          )}
        </Modal>
      )}
      <Modal
        title={formatMessage({ id: 'microsoft.notificationsLoginRequiredTitle' })}
        open={Boolean(authCompany?.platform === 'microsoft' && isMsAdminConsentRequired && importUsersAutomatically && adminConsentUrl)}
        onCancel={() => {
          setMsAdminConsentRequired(false)
          handleAutoImport(false)
        }}
        footer={null}
      >
        <Paragraph>
          <IntlMessages id="components.importUsersForm.consent.line1" />
        </Paragraph>
        <Paragraph copyable={{ text: adminConsentUrl as string }}>
          <a href={adminConsentUrl as string}>{adminConsentUrl}</a>
        </Paragraph>
      </Modal>
    </div>
  )
}

export default GeneralPage
